var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
// DocumentContext.tsx
import { createContext, useContext, useCallback, useEffect, useState, } from 'react';
import { log } from '../utils/logger';
import { DocumentationAlertMessage, DocumentStatus } from '../types/DocumentationTypes';
import { fetchLatestGeneratedCPSDocumentation, fetchLatestGeneratedPDDDocumentation } from '../api/signoAPI';
import FormResponseContext from './FormResponseContext';
import FormProgressContext, { FormStatus } from './FormProgressContext';
import { GlobalProviderContext } from './GlobalProviderContext';
import { useAuthStore } from '../application/stores/auth.store';
import { useCompanyStore } from '../application/stores/company.store';
export const DocumentContext = createContext(null);
export const DocumentContextProvider = ({ children }) => {
    // Usamos el estado de documentación y lo compartimos mediante GlobalProviderContext
    const { documentStatus, setDocumentStatus } = useContext(GlobalProviderContext);
    const [alertMessage, setAlertMessage] = useState(null);
    const [pddDocumentsFromApi, setPddDocumentsFromApi] = useState([]);
    const [cpsDocumentsFromApi, setCpsDocumentsFromApi] = useState([]);
    const { company: { isDocumentationGenerated } } = useCompanyStore();
    const getCompanyId = useAuthStore((state) => state.getCompanyId);
    const { getUserFormId } = useContext(FormResponseContext);
    const { getFormProgressStatus } = useContext(FormProgressContext);
    const userFormId = getUserFormId();
    const formProgressStatus = getFormProgressStatus();
    // Función auxiliar para hacer polling de documentos y capturar errores
    // pollForDocuments retorna los documentos en vez de setearlos directamente en el estado.
    const pollForDocuments = (fetchFn, intervalMs = 1000, timeoutMs = 60000) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const startTime = Date.now();
        while (true) {
            try {
                const response = yield fetchFn();
                const docs = response.data.documents;
                // Si es un array válido y con documentos, retornamos
                if (Array.isArray(docs) && docs.length > 0) {
                    log.debug('Documentos obtenidos: ', docs);
                    return docs;
                }
                // Si el array está vacío, paramos y retornamos array vacío
                if (Array.isArray(docs) && docs.length === 0) {
                    log.debug('Array vacío, detenemos el polling.');
                    return [];
                }
                // Si no es array pero tiene length > 0, también retornamos
                if (docs && docs.length > 0) {
                    log.debug('Documentos obtenidos (no array, pero con length): ', docs);
                    return docs;
                }
                // Si llegamos aquí, no hay documentos y no es array => retornamos vacío
                log.debug('No se encontraron documentos en la respuesta, detenemos el polling.');
                return [];
            }
            catch (err) {
                // Manejo de errores 404, 401, etc.
                if (((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) === 404) {
                    log.debug('404 - Documentos no encontrados.');
                    return [];
                }
                else if (((_b = err.response) === null || _b === void 0 ? void 0 : _b.status) === 401) {
                    log.debug('401 - No autorizado al obtener documentos.');
                    setAlertMessage(DocumentationAlertMessage.UNAUTHORIZED);
                    return [];
                }
            }
            // Control de timeout
            if (Date.now() - startTime > timeoutMs) {
                log.debug('Timeout alcanzado mientras se hacía polling de documentos');
                return [];
            }
            // Espera antes de siguiente intento
            yield new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
    });
    // Función para generar documentos con manejo de errores
    const generateDocuments = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!userFormId) {
            log.debug('generateDocuments: userFormId is undefined, aborting.');
            return;
        }
        try {
            log.debug('Generando documentos...');
            setDocumentStatus(DocumentStatus.GENERATING);
            // setAlertMessage(DocumentationAlertMessage.GENERATING);
            const timeoutMs = 60000;
            const startTime = Date.now();
            while (!isDocumentationGenerated && Date.now() - startTime < timeoutMs) {
                if (!isDocumentationGenerated) {
                    yield new Promise((resolve) => setTimeout(resolve, 1000));
                }
            }
            if (!isDocumentationGenerated) {
                setDocumentStatus(DocumentStatus.ERROR);
                setAlertMessage(DocumentationAlertMessage.NOTFOUND);
                log.error('Timeout en generateDocuments, documentación no generada.');
                return;
            }
            setDocumentStatus(DocumentStatus.GENERATED);
            // Obtenemos documentos PDD y CPS
            // Ahora usamos variables locales para los resultados:
            const pddDocs = yield pollForDocuments(() => fetchLatestGeneratedPDDDocumentation(getCompanyId()));
            const cpsDocs = yield pollForDocuments(() => fetchLatestGeneratedCPSDocumentation(getCompanyId()));
            // Actualizamos el estado con los resultados que ya tenemos en memoria local
            setPddDocumentsFromApi(pddDocs);
            setCpsDocumentsFromApi(cpsDocs);
            // Ahora sí, comprobamos si ambos están vacíos
            if (pddDocs.length === 0 && cpsDocs.length === 0) {
                setAlertMessage(DocumentationAlertMessage.NOTFOUND);
            }
            else {
                setAlertMessage(null);
            }
        }
        catch (error) {
            setDocumentStatus(DocumentStatus.ERROR);
            setAlertMessage(DocumentationAlertMessage.UNAUTHORIZED);
            log.error('Error generando documentos', error);
        }
    }), [userFormId, setDocumentStatus, getCompanyId]);
    // Función de refresco: reinicia estados y vuelve a obtener los documentos
    const refreshDocuments = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        // Reiniciamos los estados locales
        setPddDocumentsFromApi([]);
        setCpsDocumentsFromApi([]);
        // Reiniciamos el estado global y el mensaje de alerta
        setDocumentStatus(DocumentStatus.NOT_STARTED);
        // setAlertMessage(DocumentationAlertMessage.GENERATING);
        // Invocamos generateDocuments para obtener la documentación actualizada
        yield generateDocuments();
    }), [generateDocuments, setDocumentStatus]);
    // Función para obtener los documentos almacenados en el state del context
    const getDocuments = useCallback(() => {
        return {
            pddDocumentsFromApi,
            cpsDocumentsFromApi,
        };
    }, [pddDocumentsFromApi, cpsDocumentsFromApi]);
    const saveDocumentStatus = (status) => {
        setDocumentStatus(status);
    };
    // Se dispara la generación de documentos cuando el formulario se completa
    useEffect(() => {
        if (formProgressStatus === FormStatus.COMPLETED && documentStatus === DocumentStatus.NOT_STARTED) {
            generateDocuments();
        }
    }, [formProgressStatus, documentStatus, generateDocuments]);
    useEffect(() => {
        log.debug('Cambio en documentStatus:', documentStatus);
    }, [documentStatus]);
    const value = {
        generateDocuments,
        getDocuments,
        saveDocumentStatus,
        alertMessage,
        refreshDocuments
    };
    return _jsx(DocumentContext.Provider, Object.assign({ value: value }, { children: children }));
};
export default DocumentContext;
